import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import type { Assumptions } from '../types';

export interface SavedPackage {
  id: string;
  name: string;
  price: number;
  payment_term: string;
  enabled_features: string[];
  assumptions: Assumptions;
  created_at: string;
  updated_at: string;
}

export function useSavedPackages() {
  const [packages, setPackages] = useState<SavedPackage[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch('/api/packages');
      if (res.ok) setPackages(await res.json());
    } catch { /* DB not available */ }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const savePackage = useCallback(async (data: {
    name: string; price: number; paymentTerm: string;
    enabledFeatures: string[]; assumptions: Assumptions;
  }) => {
    const res = await apiFetch('/api/packages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to save');
    const pkg = await res.json();
    setPackages(prev => [pkg, ...prev]);
    return pkg as SavedPackage;
  }, []);

  const deletePackage = useCallback(async (id: string) => {
    await apiFetch(`/api/packages/${id}`, { method: 'DELETE' });
    setPackages(prev => prev.filter(p => p.id !== id));
  }, []);

  return { packages, loading, refresh, savePackage, deletePackage };
}
